import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InstallationFeaturesStore, WallboxChargingSubstatus } from '@store';
import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  IsStringValueProperty,
  WallboxSessionCharging,
  WallboxStatus,
  WallboxSubstatus,
} from '@vi-iot/client';

import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SUFFIX } from 'src/app/shared/common/units';
import { WallboxSystem } from '../wallbox-system-helper';

@Component({
  selector: 'app-wallbox-system-overlay',
  templateUrl: './wallbox-system-overlay.component.html',
  styleUrls: ['./wallbox-system-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WallboxSystemOverlayComponent implements OnInit, OnDestroy {
  readonly SUFFIX = SUFFIX;
  readonly featurePropertyKey = FeaturePropertyKey;
  chargingTranslationKey = '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING.';

  deviceThirdpartyManufacturer$: Observable<Feature<IsStringValueProperty, never>>;
  deviceThirdpartyModel$: Observable<Feature<IsStringValueProperty, never>>;
  deviceThirdpartySoftware$: Observable<Feature<IsStringValueProperty, never>>;
  deviceEtn$: Observable<Feature<IsStringValueProperty, never>>;
  chargingPower$: Observable<Feature<IsNumberValueProperty, never>>;
  displayedStatus$: Observable<Feature<WallboxStatus, never>>; // not present in wiki
  sessionCharging$: Observable<Feature<WallboxSessionCharging, never>>;
  sessionPhases$: Observable<Feature<IsStringValueProperty, never>>;
  wallboxDisplayedStatus$: Observable<Feature<WallboxStatus, never>>;
  displayedSubStatus$: Observable<Feature<WallboxSubstatus, never>>;

  displayedStatusDescription: string;
  FeatureNames = FeatureNames;
  phaseLabel = '';
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.deviceThirdpartyManufacturer$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceThirdpartyManufacturer
    );
    this.deviceThirdpartyModel$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceThirdpartyModel
    );
    this.deviceThirdpartySoftware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceThirdpartySoftware
    );
    this.deviceEtn$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceEtn
    );
    this.chargingPower$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxDisplayedPower
    );
    this.displayedStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      WallboxStatus,
      never
    >(FeatureNames.wallboxDisplayedStatus);
    this.displayedSubStatus$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<WallboxSubstatus, never>(
        FeatureNames.wallboxDisplayedSubstatus
      )
      .pipe(
        tap((feature) => {
          this.setDisplayStatusLabels(feature?.properties?.substatus?.value);
        })
      );

    this.sessionCharging$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxSessionCharging
    );
    this.wallboxDisplayedStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxDisplayedStatus
    );
    this.sessionPhases$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxSessionPhases
    );

    combineLatest([this.sessionPhases$, this.wallboxDisplayedStatus$])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([phasesFeature, wallBoxChargingStatus]) => {
        const phase = phasesFeature?.properties?.value?.value;
        const chargingStatus = wallBoxChargingStatus?.properties?.status?.value;
        const phaseLabelTranslation = this.translate.instant(
          WallboxSystem.getChargingPhaseStatusTranslationKey(phase, chargingStatus)
        );
        this.phaseLabel = phaseLabelTranslation?.length ? `(${phaseLabelTranslation})` : '';
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setDisplayStatusLabels(status: WallboxChargingSubstatus | string): void {
    switch (status) {
      case WallboxChargingSubstatus.CHARGING_WITH_REDUCED_POWER:
        this.displayedStatusDescription = `(${this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING.CHARGING_WITH_REDUCED_POWER'
        )})`;
        break;

      case WallboxChargingSubstatus.OVERLOAD_PROTECTION_ACTIVE:
        this.displayedStatusDescription = `(${this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING.OVERLOAD_PROTECTION_ACTIVE'
        )})`;
        break;

      case WallboxChargingSubstatus.PV_EXCESS_NOT_SUFFICIENT:
        this.displayedStatusDescription = `(${this.translate.instant(
          '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING.PV_EXCESS_NOT_SUFFICIENT'
        )})`;
        break;
      default:
        this.displayedStatusDescription = '';
    }
  }
}
